import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { EnhancedCard } from '../layout/EnhancedCard';
import type { Character } from '../../store/slices/charactersSlice';
import type { DivineOrbPerHourDto, DivineOrbsGained24hDto } from '../../services/itemService';

type GroupBy = 'build' | 'stage' | 'buildAndStage';
type SortBy = 'key' | 'characterCount' | 'avgDivPerHour' | 'avgDivsGained24h' | 'totalDivsGained24h';
type Order = 'asc' | 'desc';

interface GroupedStats {
  key: string;
  build: string;
  stage: string;
  characterCount: number;
  avgDivPerHour: number;
  avgDivsGained24h: number;
  totalDivsGained24h: number;
}

interface BuildAnalysisViewProps {
  characters: Character[];
  divineOrbsPerHour: Record<string, DivineOrbPerHourDto>;
  divineOrbsGained24h: Record<string, DivineOrbsGained24hDto>;
}

const BuildAnalysisView: React.FC<BuildAnalysisViewProps> = ({ characters, divineOrbsPerHour, divineOrbsGained24h }) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('build');
  const [sortBy, setSortBy] = useState<SortBy>('avgDivPerHour');
  const [order, setOrder] = useState<Order>('desc');

  const handleSort = (property: SortBy) => {
    const isAsc = sortBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const groupedStats = useMemo(() => {
    const groups = new Map<string, { characters: Character[]; build: string; stage: string }>();

    characters.forEach(char => {
      const build = char.build || 'Unknown';
      const stage = char.stage || 'Unknown';

      let key: string;
      switch (groupBy) {
        case 'build':
          key = build;
          break;
        case 'stage':
          key = stage;
          break;
        case 'buildAndStage':
          key = `${build} / ${stage}`;
          break;
      }

      if (!groups.has(key)) {
        groups.set(key, { characters: [], build, stage });
      }
      groups.get(key)!.characters.push(char);
    });

    const stats: GroupedStats[] = [];

    groups.forEach((group, key) => {
      const divPerHourValues = group.characters
        .map(char => divineOrbsPerHour[char.id]?.divPerHour)
        .filter((v): v is number => v !== undefined && v > 0);

      const gained24hValues = group.characters
        .map(char => divineOrbsGained24h[char.id]?.divineOrbsGained)
        .filter((v): v is number => v !== undefined && v > 0);

      const avgDivPerHour = divPerHourValues.length > 0 ? divPerHourValues.reduce((a, b) => a + b, 0) / divPerHourValues.length : 0;

      const totalDivsGained24h = gained24hValues.reduce((a, b) => a + b, 0);
      const avgDivsGained24h = gained24hValues.length > 0 ? totalDivsGained24h / gained24hValues.length : 0;

      stats.push({
        key,
        build: group.build,
        stage: group.stage,
        characterCount: group.characters.length,
        avgDivPerHour: Math.round(avgDivPerHour * 100) / 100,
        avgDivsGained24h: Math.round(avgDivsGained24h * 10) / 10,
        totalDivsGained24h: Math.round(totalDivsGained24h * 10) / 10,
      });
    });

    // Sort
    stats.sort((a, b) => {
      const valA = a[sortBy] as number | string;
      const valB = b[sortBy] as number | string;
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return stats;
  }, [characters, divineOrbsPerHour, divineOrbsGained24h, groupBy, sortBy, order]);

  // Percentile-based color for avg div/hr
  const getDivPerHourColor = (value: number): string => {
    if (value <= 0 || groupedStats.length === 0) return 'text.secondary';
    const sortedValues = groupedStats.map(s => s.avgDivPerHour).filter(v => v > 0).sort((a, b) => a - b);
    if (sortedValues.length === 0) return 'text.secondary';
    const position = sortedValues.findIndex(v => v >= value);
    const percentile = position / sortedValues.length;
    if (percentile < 0.25) return '#f44336';
    if (percentile < 0.5) return '#ff9800';
    if (percentile < 0.75) return '#2196f3';
    return '#4caf50';
  };

  if (characters.length === 0) {
    return (
      <Typography variant='body1' color='text.secondary'>
        No characters to analyze.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h6'>Build & Strategy Analysis</Typography>
        <ToggleButtonGroup value={groupBy} exclusive onChange={(_, value) => value && setGroupBy(value)} size='small'>
          <ToggleButton value='build'>Build</ToggleButton>
          <ToggleButton value='stage'>Strategy</ToggleButton>
          <ToggleButton value='buildAndStage'>Build + Strategy</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <EnhancedCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortBy === 'key'} direction={sortBy === 'key' ? order : 'asc'} onClick={() => handleSort('key')}>
                    {groupBy === 'build' ? 'Build' : groupBy === 'stage' ? 'Strategy' : 'Build / Strategy'}
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  <TableSortLabel
                    active={sortBy === 'characterCount'}
                    direction={sortBy === 'characterCount' ? order : 'desc'}
                    onClick={() => handleSort('characterCount')}
                  >
                    Characters
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  <TableSortLabel
                    active={sortBy === 'avgDivPerHour'}
                    direction={sortBy === 'avgDivPerHour' ? order : 'desc'}
                    onClick={() => handleSort('avgDivPerHour')}
                  >
                    Avg Div/Hr
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  <TableSortLabel
                    active={sortBy === 'avgDivsGained24h'}
                    direction={sortBy === 'avgDivsGained24h' ? order : 'desc'}
                    onClick={() => handleSort('avgDivsGained24h')}
                  >
                    Avg 24h Gain
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  <TableSortLabel
                    active={sortBy === 'totalDivsGained24h'}
                    direction={sortBy === 'totalDivsGained24h' ? order : 'desc'}
                    onClick={() => handleSort('totalDivsGained24h')}
                  >
                    Total 24h Gain
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedStats.map(row => (
                <TableRow key={row.key}>
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                      {row.key}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>{row.characterCount}</TableCell>
                  <TableCell align='right'>
                    <Typography variant='body2' sx={{ color: getDivPerHourColor(row.avgDivPerHour), fontWeight: 'bold' }}>
                      {row.avgDivPerHour > 0 ? row.avgDivPerHour.toFixed(2) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                      {row.avgDivsGained24h > 0 ? row.avgDivsGained24h.toFixed(1) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                      {row.totalDivsGained24h > 0 ? row.totalDivsGained24h.toFixed(1) : 'N/A'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {groupedStats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </EnhancedCard>
    </Box>
  );
};

export default BuildAnalysisView;
